import {Observable} from 'rxjs';
import {inject, Injectable} from '@angular/core';
import {HttpParams} from '@angular/common/http';
import {CarouselDisplay, CatalogRecord, CatalogRecordAvailabilityDetail, SeparatedSearchParams} from '@store/catalog/catalog.types';
import {ListData} from '@aviary/store/common/common.types';
import {buildQueryParamsFrom} from '@aviary/store/common/list-data.helpers';
import {Params} from '@angular/router';
import {ApiService} from '@aviary/services/api.service';
import {RequestParams} from '@aviary/services/api.service.types';
import {separateSearchFiltersFromQueryParams} from '@store/catalog/catalog.helpers';

@Injectable({providedIn: 'root'})
export class CatalogService {

  private readonly apiService: ApiService = inject(ApiService);

  public loadCatalogRecord(catalogRecordId: number): Observable<CatalogRecord> {
    const endpoint: string = `public/v2/catalog-records/${catalogRecordId}`;

    return this.apiService.getSingleObject(endpoint);
  }

  public loadCatalogAvailability(catalogRecordId: number): Observable<CatalogRecordAvailabilityDetail> {
    const endpoint: string = `public/v2/catalog-records/${catalogRecordId}/availability`;

    return this.apiService.getSingleObject(endpoint);
  }

  public loadCatalogRelatedItems(catalogRecordId: number): Observable<CatalogRecord[]> {
    const endpoint: string = `public/v1/catalog-records/${catalogRecordId}/related`;

    return this.apiService.get(endpoint);
  }

  public getCatalogCarousels(): Observable<CarouselDisplay[]> {
    const endpoint: string = 'public/v1/carousel-display';

    return this.apiService.get(endpoint);
  }

  public searchCatalogRecords(queryParams: Params): Observable<ListData<CatalogRecord>> {
    const endpoint: string = 'public/v2/catalog-records/elastic-search';
    const {filters: body, otherQueryParams}: SeparatedSearchParams = separateSearchFiltersFromQueryParams(queryParams);
    const params: HttpParams = buildQueryParamsFrom(otherQueryParams);

    return this.apiService.postAndReturnListData(endpoint, body, {params});
  }

  public getFavorites(queryParams: Params): Observable<ListData<CatalogRecord>> {
    const endpoint: string = 'patrons/v5/favorites';
    const params: HttpParams = buildQueryParamsFrom(queryParams);

    return this.apiService.getListData(endpoint, {params});
  }

  public getFavoritesIds(): Observable<number[]> {
    const endpoint: string = 'patrons/v5/favorites/ids';

    return this.apiService.getOptionalObjects(endpoint);
  }

  public addFavorite(catalogRecordId: number): Observable<void> {
    const endpoint: string = 'patrons/v2/favorites';
    const params: RequestParams = {catalogRecordId: String(catalogRecordId)};

    return this.apiService.putWithoutBody(endpoint, {params});
  }

  public removeFavorite(catalogRecordId: number): Observable<void> {
    const endpoint: string = 'patrons/v5/favorite';
    const params: RequestParams = {catalogRecordId: String(catalogRecordId)};

    return this.apiService.delete(endpoint, {params});
  }
}
